import {
    deepClone
} from "../utils/objects.js";

/**
 * Manages the game log entries and formatting.
 */
export default class GameLog {

    /**
     * Entry types supported by the log.
     */
    static types = [
        "game-start",
        "phase",
        "execution",
        "death",
        "note",
        "game-end"
    ];

    /**
     * Creates an instance of {@link GameLog}.
     *
     * @param {Store} store
     *        Store instance used for persistence.
     */
    constructor(store) {

        /**
         * Store instance for persistence.
         * @type {Store}
         */
        this.store = store;

        /**
         * In-memory log data.
         * @type {Object}
         */
        this.data = this.store.getLog();
        this.data.entries = this.data.entries.filter(
            (entry) => this.isKnownType(entry)
        );
        this.persist();

    }

    /**
     * Persists the current log data to the store.
     */
    persist() {
        this.store.setLog(this.data);
    }

    /**
     * Generates a simple unique ID.
     *
     * @return {String}
     *         A unique identifier.
     */
    generateId() {
        return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    }

    /**
     * Starts a new game log.
     *
     * @param {String} gameName
     *        Name of the script.
     * @param {String} scriptId
     *        Script identifier.
     * @param {Array.<Object>} players
     *        List of players with character data.
     */
    startGame(gameName, scriptId, players) {

        this.data = deepClone(this.store.constructor.defaults.log);
        this.data.gameName = gameName;
        this.data.scriptId = scriptId;
        this.data.players = players.map((player) => ({
            characterId: player.characterId || "",
            characterName: player.characterName || "",
            playerName: player.playerName || "",
            team: player.team || ""
        }));
        this.data.startedAt = new Date().toISOString();
        this.addEntry("game-start", {
            gameName,
            scriptId,
            playerCount: players.length
        });

    }

    /**
     * Adds a new log entry.
     *
     * @param {String} type
     *        Entry type.
     * @param {Object} data
     *        Entry-specific data.
     * @param {String} [note=""]
     *        Optional manual note.
     * @return {Object}
     *         The created entry.
     */
    addEntry(type, data = {}, note = "") {

        if (!this.constructor.types.includes(type)) {
            return null;
        }

        const entry = {
            id: this.generateId(),
            type,
            day: this.data.currentDay,
            phase: this.data.currentPhase,
            timestamp: new Date().toISOString(),
            data: deepClone(data),
            note: note || ""
        };

        if (type === "phase") {
            this.data.currentDay = data.day || this.data.currentDay;
            this.data.currentPhase = data.phase || this.data.currentPhase;
            entry.day = this.data.currentDay;
            entry.phase = this.data.currentPhase;
        }

        this.data.entries.push(entry);
        this.persist();

        return entry;

    }

    /**
     * Updates an existing entry.
     *
     * @param {String} id
     *        Entry ID.
     * @param {Object} updates
     *        Fields to update.
     * @return {Object|null}
     *         The updated entry or null if not found.
     */
    updateEntry(id, updates) {

        const entry = this.data.entries.find((item) => item.id === id);

        if (!entry) {
            return null;
        }

        if (updates.data) {
            entry.data = deepClone(updates.data);
        }

        if (typeof updates.note === "string") {
            entry.note = updates.note;
        }

        entry.timestamp = new Date().toISOString();
        this.persist();

        return entry;

    }

    /**
     * Deletes an entry by ID.
     *
     * @param {String} id
     *        Entry ID.
     * @return {Boolean}
     *         True if an entry was deleted.
     */
    deleteEntry(id) {

        const index = this.data.entries.findIndex((item) => item.id === id);

        if (index < 0) {
            return false;
        }

        this.data.entries.splice(index, 1);
        this.persist();

        return true;

    }

    /**
     * Moves an entry from one position to another.
     *
     * @param {Number} fromIndex
     *        Current index of the entry.
     * @param {Number} toIndex
     *        Target index.
     * @return {Boolean}
     *         True if the move was performed.
     */
    moveEntry(fromIndex, toIndex) {

        const entries = this.data.entries;

        if (
            fromIndex < 0 || fromIndex >= entries.length
            || toIndex < 0 || toIndex >= entries.length
            || fromIndex === toIndex
        ) {
            return false;
        }

        const [entry] = entries.splice(fromIndex, 1);
        entries.splice(toIndex, 0, entry);
        this.persist();

        return true;

    }

    /**
     * Ends the game and records the winner.
     *
     * @param {String} winner
     *        `good`, `evil` or `none`.
     * @param {String} [note=""]
     *        Optional final note.
     */
    endGame(winner, note = "") {

        this.data.winner = winner;
        this.data.endedAt = new Date().toISOString();
        this.addEntry("game-end", {
            winner
        }, note);

    }

    /**
     * Returns all log entries.
     *
     * @return {Array.<Object>}
     *         Log entries.
     */
    getEntries() {
        return deepClone(this.data.entries.filter(
            (entry) => this.isKnownType(entry)
        ));
    }

    /**
     * Checks whether the entry's type is a known log entry type.
     *
     * @param  {Object} entry
     *         Log entry to check.
     * @return {Boolean}
     *         true if the entry type is known, false otherwise.
     */
    isKnownType(entry) {
        return this.constructor.types.includes(entry.type);
    }

    /**
     * Gets the current player names from the log.
     *
     * @return {Array.<String>}
     *         Player names.
     */
    getPlayerNames() {
        return this.data.players
            .map((player) => player.playerName)
            .filter(Boolean);
    }

    /**
     * Clears the entire log.
     */
    clear() {
        this.data = deepClone(this.store.constructor.defaults.log);
        this.persist();
    }

    /**
     * Reloads in-memory data from the store (e.g. after external clear).
     */
    reload() {
        this.data = this.store.getLog();
    }

    /**
     * Formats the log as text for display or Telegram.
     *
     * @return {String}
     *         Formatted log text.
     */
    formatText(telegram = false) {

        const entries = this.getEntries();
        const lines = [];
        const date = this.data.startedAt
            ? new Date(this.data.startedAt).toLocaleDateString("ru-RU")
            : "";

        lines.push(`🎭 ${this.data.gameName || "Blood on the Clocktower"}`);

        if (date) {
            lines.push(`📅 ${date}`);
        }

        if (this.data.players.length) {
            const playerList = this.data.players
                .map((player) => this.formatPlayerLine(player, telegram))
                .filter(Boolean)
                .join("\n");
            lines.push(`👥\n${playerList}`);
        }

        lines.push("");

        let phaseSeen = false;
        entries.forEach((entry) => {
            if (entry.type === "phase") {
                if (entry.data.phase === "night" && phaseSeen
                        && lines[lines.length - 1] !== "") {
                    lines.push("");
                }
                phaseSeen = true;
            }
            lines.push(this.formatEntry(entry, telegram));
        });

        if (["good", "evil"].includes(this.data.winner)) {
            const side = this.data.winner === "good" ? "добра" : "зла";
            const emoji = telegram ? ` ${this.emojiMarker(this.data.winner)}` : "";
            const winnerText = `Победа ${side}${emoji}`;
            lines.push("");
            lines.push(`🏆 ${winnerText}`);
        }

        return lines.join("\n").trim();

    }

    /**
     * Formats a single entry as a text line.
     *
     * @param {Object} entry
     *        Log entry.
     * @return {String}
     *         Formatted line.
     */
    formatEntry(entry, telegram = false) {

        const {
            type,
            data,
            note
        } = entry;
        let line = "";
        const players = this.data.players.filter((player) => player.playerName === data.playerName);
        const characterId = data.characterId || (players.length === 1 ? players[0].characterId : "");
        const emoji = telegram ? this.emojiMarker(characterId) : "";
        const playerLabel = [data.playerName, emoji].filter(Boolean).join(" ");

        switch (type) {

        case "game-start":
            line = `▶️ Начало игры: ${data.gameName}`;
            break;

        case "phase":
            line = data.phase === "night"
                ? `🌙 Ночь ${data.day}`
                : `🌅 День ${data.day}`;
            break;

        case "execution":
            line = data.playerName
                ? `⚔️ Казнён ${playerLabel}`
                : `⚔️ Никто не казнён`;
            break;

        case "death": {
            const reasonLabels = {
                execution: I18N.gameLogReasonExecution || "Казнён",
                demon: I18N.gameLogReasonDemon || "Убит демоном",
                other: I18N.gameLogReasonOther || "Другое"
            };
            let reasonText = reasonLabels[data.reason] || data.reason || "";
            if (data.reason === "other" && data.reasonCustom) {
                reasonText = data.reasonCustom;
            }
            line = reasonText
                ? `☠️ ${playerLabel} умер (${reasonText})`
                : `☠️ ${playerLabel} умер`;
            break;
        }

        case "note":
            line = `📝 ${data.text || note}`;
            break;

        case "game-end":
            line = `🏁 Игра окончена`;
            break;

        default:
            line = `• ${type}`;

        }

        if (note && type !== "note") {
            line += ` — ${note}`;
        }

        return line;

    }

    /**
     * Formats a single player line for the players list.
     *
     * @param  {Object} player
     *         Player data from the log.
     * @param  {Boolean} telegram
     *         true to include a Telegram emoji marker for the character.
     * @return {String}
     *         Formatted player line.
     */
    formatPlayerLine(player, telegram = false) {

        const name = player.playerName || "";
        const role = player.characterName || player.characterId || "";
        const teamLabel = {
            townsfolk: "гор.",
            outsider: "Изгой",
            minion: "присп.",
            demon: "Демон",
            traveller: "Путешественник",
            fabled: "Легенда"
        }[player.team] || I18N[player.team] || player.team || "";
        const details = [telegram ? this.emojiMarker(player.characterId) : "", teamLabel]
            .filter(Boolean).join(" ");
        let text = "";
        if (name && role) {
            text = `${name} — ${role}`;
        } else if (name) {
            text = name;
        } else if (role) {
            text = role;
        }
        if (details) {
            text = text ? `${text} (${details})` : `(${details})`;
        }
        return text;

    }

    /**
     * Converts the character ID into a Telegram emoji placeholder marker.
     *
     * @param  {String} characterId
     *         Character ID to mark.
     * @return {String}
     *         The emoji marker, or an empty string.
     */
    emojiMarker(characterId) {
        const id = (characterId || "").toLowerCase().replace(/[^a-z0-9]/g, "");
        return id ? `[[pg:${id}]]` : "";
    }

}
